'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ArrowLeft, Clock, CheckCircle } from 'lucide-react'
import { toast } from 'sonner'

import { api, ApiError } from '@/lib/api'
import { API } from '@/config/env'
import type { Category, CatalogSubmission, ApiSuccessResponse } from '@/shared/types'
import { Button } from '@/components/ui/button'

// ─────────────────────────────────────────────────────────────────────────────
// RATE LIMIT HELPERS  (15s cooldown, 5 per day — tracked in localStorage)
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY_LAST    = 'diecastshowcase_catalog_sub_last'
const STORAGE_KEY_DATE    = 'diecastshowcase_catalog_sub_date'
const STORAGE_KEY_COUNT   = 'diecastshowcase_catalog_sub_count'
const COOLDOWN_MS         = 15_000
const DAILY_LIMIT         = 5

function todayStr() {
  return new Date().toISOString().slice(0, 10)
}

function getRateLimitState(): { cooldownLeft: number; todayCount: number } {
  if (typeof window === 'undefined') return { cooldownLeft: 0, todayCount: 0 }
  const last  = Number(localStorage.getItem(STORAGE_KEY_LAST) ?? 0)
  const date  = localStorage.getItem(STORAGE_KEY_DATE) ?? ''
  const count = date === todayStr() ? Number(localStorage.getItem(STORAGE_KEY_COUNT) ?? 0) : 0
  const cooldownLeft = Math.max(0, COOLDOWN_MS - (Date.now() - last))
  return { cooldownLeft, todayCount: count }
}

function recordSubmission() {
  const today = todayStr()
  const date  = localStorage.getItem(STORAGE_KEY_DATE) ?? ''
  const count = date === today ? Number(localStorage.getItem(STORAGE_KEY_COUNT) ?? 0) : 0
  localStorage.setItem(STORAGE_KEY_LAST,  String(Date.now()))
  localStorage.setItem(STORAGE_KEY_DATE,  today)
  localStorage.setItem(STORAGE_KEY_COUNT, String(count + 1))
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES: { value: Category | ''; label: string }[] = [
  { value: '',             label: 'Select category…' },
  { value: 'cars',        label: 'Cars' },
  { value: 'trucks',      label: 'Trucks' },
  { value: 'motorcycles', label: 'Motorcycles' },
  { value: 'aircraft',    label: 'Aircraft' },
  { value: 'ships',       label: 'Ships' },
  { value: 'other',       label: 'Other' },
]

const SCALES = ['1:64', '1:50', '1:48', '1:43', '1:32', '1:24', '1:18']

export default function CatalogSubmitPage() {
  const router = useRouter()

  // Form state
  const [productName,     setProductName]     = useState('')
  const [brandName,       setBrandName]       = useState('')
  const [scale,           setScale]           = useState('')
  const [category,        setCategory]        = useState<Category | ''>('')
  const [carManufacturer, setCarManufacturer] = useState('')
  const [year,            setYear]            = useState('')
  const [color,           setColor]           = useState('')
  const [productCode,     setProductCode]     = useState('')
  const [description,     setDescription]     = useState('')

  // Submit state
  const [submitting,     setSubmitting]    = useState(false)
  const [cooldownLeft,   setCooldownLeft]  = useState(0)
  const [todayCount,     setTodayCount]    = useState(0)
  const [submitted,      setSubmitted]     = useState<CatalogSubmission | null>(null)

  // Hydrate rate limit from localStorage
  useEffect(() => {
    const { cooldownLeft: cd, todayCount: cnt } = getRateLimitState()
    setCooldownLeft(cd)
    setTodayCount(cnt)
  }, [])

  // Tick the cooldown timer
  useEffect(() => {
    if (cooldownLeft <= 0) return
    const id = setInterval(() => {
      setCooldownLeft(prev => {
        const next = Math.max(0, prev - 1000)
        return next
      })
    }, 1000)
    return () => clearInterval(id)
  }, [cooldownLeft])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!productName.trim()) {
      toast.error('Product name is required')
      return
    }

    const { cooldownLeft: cd, todayCount: cnt } = getRateLimitState()
    if (cd > 0) {
      toast.error(`Please wait ${Math.ceil(cd / 1000)}s before submitting again`)
      return
    }
    if (cnt >= DAILY_LIMIT) {
      toast.error('You\'ve reached the daily limit of 5 submissions. Try again tomorrow.')
      return
    }

    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        submissionType: 'detailed',
        productName: productName.trim(),
      }
      if (brandName.trim())       body.brandName       = brandName.trim()
      if (scale)                  body.scale           = scale
      if (category)               body.category        = category
      if (carManufacturer.trim()) body.carManufacturer = carManufacturer.trim()
      if (year.trim())            body.year            = year.trim()
      if (color.trim())           body.color           = color.trim()
      if (productCode.trim())     body.productCode     = productCode.trim()
      if (description.trim())     body.description     = description.trim()

      const res = await api.post<ApiSuccessResponse<CatalogSubmission>>(
        API.CATALOG_SUBMISSIONS,
        body
      )
      recordSubmission()
      const { cooldownLeft: newCd, todayCount: newCnt } = getRateLimitState()
      setCooldownLeft(newCd)
      setTodayCount(newCnt)
      setSubmitted(res.data)
      toast.success('Submission received! Our team will review it.')
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          toast.error('Rate limit reached — please wait before submitting again.')
        } else if (err.details?.length) {
          toast.error(err.details.map(d => d.message).join(', '))
        } else {
          toast.error(err.message)
        }
      } else {
        toast.error('Submission failed. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ── Success screen ─────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-xl px-4 py-16 text-center sm:px-6">
          <div className="mb-4 flex justify-center">
            <div className="flex size-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
              <CheckCircle className="size-8" />
            </div>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-foreground">Submission received!</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            <strong className="text-foreground">{submitted.productName}</strong> has been submitted
            for review. Our team will validate it shortly.
          </p>

          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link href={`/catalog/submissions/${submitted.id}`}>
              <Button>Track submission status</Button>
            </Link>
            <Link href="/catalog/search">
              <Button variant="outline">Back to catalog</Button>
            </Link>
          </div>

          {todayCount < DAILY_LIMIT && (
            <p className="text-xs text-muted-foreground">
              {DAILY_LIMIT - todayCount} submission{DAILY_LIMIT - todayCount !== 1 ? 's' : ''} remaining today
            </p>
          )}
        </div>
      </div>
    )
  }

  const isRateLimited = cooldownLeft > 0 || todayCount >= DAILY_LIMIT

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        {/* Back */}
        <Link
          href="/catalog/search"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to catalog
        </Link>

        <h1 className="mb-1 text-2xl font-bold text-foreground">Submit a Product</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Help grow the catalog by submitting a diecast product for review.
        </p>

        {/* Rate limit banner */}
        {todayCount >= DAILY_LIMIT ? (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <Clock className="mt-0.5 size-4 shrink-0" />
            <span>You&apos;ve reached the daily limit of {DAILY_LIMIT} submissions. Come back tomorrow!</span>
          </div>
        ) : cooldownLeft > 0 ? (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
            <Clock className="size-4 shrink-0" />
            <span>Cooldown: {Math.ceil(cooldownLeft / 1000)}s before next submission</span>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Product name — required */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Product Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              required
              value={productName}
              onChange={e => setProductName(e.target.value)}
              placeholder="e.g. 1969 Ford Mustang Boss 429"
              maxLength={500}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Two-column grid for optional fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Brand</label>
              <input
                type="text"
                value={brandName}
                onChange={e => setBrandName(e.target.value)}
                placeholder="e.g. Hot Wheels"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Scale</label>
              <select
                value={scale}
                onChange={e => setScale(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select scale…</option>
                {SCALES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value as Category | '')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Car Manufacturer</label>
              <input
                type="text"
                value={carManufacturer}
                onChange={e => setCarManufacturer(e.target.value)}
                placeholder="e.g. Ford, Toyota"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Year</label>
              <input
                type="text"
                value={year}
                onChange={e => setYear(e.target.value)}
                placeholder="e.g. 1969 or 1969-1970"
                maxLength={50}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Color</label>
              <input
                type="text"
                value={color}
                onChange={e => setColor(e.target.value)}
                placeholder="e.g. Spectraflame Red"
                maxLength={100}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Product Code</label>
              <input
                type="text"
                value={productCode}
                onChange={e => setProductCode(e.target.value)}
                placeholder="e.g. HW-1234"
                maxLength={50}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Additional Notes
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Any additional details that might help our team verify this product…"
              rows={3}
              maxLength={2000}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-xs text-muted-foreground">
              {todayCount > 0
                ? `${DAILY_LIMIT - todayCount} of ${DAILY_LIMIT} submissions remaining today`
                : `Up to ${DAILY_LIMIT} submissions per day`}
            </p>
            <Button
              type="submit"
              disabled={submitting || isRateLimited || !productName.trim()}
              className="min-w-32"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
